import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { LiveFeed } from '../components/LiveFeed';
import { ConfidenceMetrics } from '../components/ConfidenceMetrics';
import { InstrumentTable } from '../components/InstrumentTable';
import { MissingInstrumentModal } from '../components/MissingInstrumentModal';
import { ManualOverrideForm } from '../components/ManualOverrideForm';
import { FinalSummary } from '../components/FinalSummary';
import { ScanErrorModal } from '../components/ScanErrorModal';
import { UnsavedChangesModal } from '../components/UnsavedChangesModal';
import { AIChatAssistant } from '../components/AIChatAssistant';
import { useCaseContext } from '../contexts/CaseContext';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
export function DashboardPage() {
  const navigate = useNavigate();
  const {
    instruments,
    setInstruments,
    caseDetails,
    metrics,
    setMetrics,
    lastScanTime,
    setLastScanTime,
    hasReviewedChecklist,
    setHasReviewedChecklist,
    incrementScanAttempts,
    hasUnsavedChanges
  } = useCaseContext();
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStage, setScanStage] = useState('');
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showScanError, setShowScanError] = useState(false);
  const [scanErrorType, setScanErrorType] = useState<'camera' | 'network' | 'timeout' | 'processing'>('processing');
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [hasTriggeredMissing, setHasTriggeredMissing] = useState(false);
  const [finalizeUnlockTime, setFinalizeUnlockTime] = useState<number | null>(null);
  const missingInstruments = instruments.filter(i => i.status === 'missing');
  const missingCount = missingInstruments.length;
  const isAllClear = instruments.every(i => i.status === 'ok' || i.status === 'override') && instruments.some(i => i.status !== 'pending');
  const canFinalize = isAllClear && missingCount === 0 && hasReviewedChecklist && (!finalizeUnlockTime || Date.now() >= finalizeUnlockTime);
  const handleTriggerScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    incrementScanAttempts();
    // Simulate realistic scan stages
    const stages = [{
      progress: 30,
      stage: 'Capturing image...',
      duration: 800
    }, {
      progress: 60,
      stage: 'Detecting objects...',
      duration: 1200
    }, {
      progress: 85,
      stage: 'Classifying instruments...',
      duration: 800
    }, {
      progress: 100,
      stage: 'Validating results...',
      duration: 400
    }];
    let currentStage = 0;
    const runStage = () => {
      if (currentStage >= stages.length) {
        completeScan();
        return;
      }
      const stage = stages[currentStage];
      setScanStage(stage.stage);
      const startProgress = currentStage === 0 ? 0 : stages[currentStage - 1].progress;
      const increment = (stage.progress - startProgress) / (stage.duration / 50);
      const interval = setInterval(() => {
        setScanProgress(prev => {
          const next = prev + increment;
          if (next >= stage.progress) {
            clearInterval(interval);
            currentStage++;
            setTimeout(runStage, 100);
            return stage.progress;
          }
          return next;
        });
      }, 50);
    };
    // Add timeout protection (30 seconds)
    const timeout = setTimeout(() => {
      if (isScanning) {
        setIsScanning(false);
        setScanErrorType('timeout');
        setShowScanError(true);
      }
    }, 30000);
    runStage();
    return () => clearTimeout(timeout);
  };
  const completeScan = () => {
    setIsScanning(false);
    setScanStage('');
    const now = new Date();
    setLastScanTime(now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }));
    if (!hasTriggeredMissing) {
      const newInstruments = instruments.map(inst => ({
        ...inst,
        status: inst.id === '1' ? 'missing' : 'ok',
        found: inst.id === '1' ? 0 : inst.expected
      }));
      const detectedCount = newInstruments.filter(i => i.status === 'ok').length;
      setInstruments(newInstruments);
      setMetrics({
        certainty: 96.8,
        detectedCount,
        latency: 38
      });
      setShowMissingModal(true);
      setHasTriggeredMissing(true);
      setHasReviewedChecklist(false);
    } else {
      const detectedCount = instruments.filter(i => i.status === 'ok').length;
      setMetrics({
        certainty: 99.2,
        detectedCount,
        latency: 35
      });
      if (missingCount > 0) {
        setShowMissingModal(true);
      }
    }
    // Start 3-second unlock timer for finalize button
    if (isAllClear && missingCount === 0) {
      setFinalizeUnlockTime(Date.now() + 3000);
    }
  };
  const handleRescan = () => {
    setShowMissingModal(false);
    handleTriggerScan();
  };
  const handleOverrideClick = () => {
    setShowMissingModal(false);
    setShowOverrideForm(true);
  };
  const handleResolveOverride = (reason: string, notes: string) => {
    const missingId = missingInstruments[0].id;
    setInstruments(prev => prev.map(inst => inst.id === missingId ? {
      ...inst,
      status: 'override',
      found: inst.expected
    } : inst));
    setShowOverrideForm(false);
    // Recalculate metrics
    const detectedCount = instruments.filter(i => i.status === 'ok' || i.status === 'override').length;
    setMetrics(prev => ({
      ...prev,
      detectedCount
    }));
  };
  const handleFinalize = () => {
    if (!hasReviewedChecklist) {
      alert('Please review the instrument checklist before finalizing.');
      return;
    }
    if (finalizeUnlockTime && Date.now() < finalizeUnlockTime) {
      return;
    }
    setShowSummary(true);
  };
  const handleManualEntry = () => {
    setShowScanError(false);
    alert('Manual entry mode would open here');
  };
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S: Trigger Scan
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isScanning) handleTriggerScan();
      }
      // Ctrl+F or Cmd+F: Finalize (if enabled)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        if (canFinalize) handleFinalize();
      }
      // Escape: Close modals
      if (e.key === 'Escape') {
        if (showMissingModal) setShowMissingModal(false);
        if (showOverrideForm) setShowOverrideForm(false);
        if (showScanError) setShowScanError(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isScanning, canFinalize, showMissingModal, showOverrideForm, showScanError]);
  // Block navigation if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  if (showSummary) {
    return <FinalSummary caseDetails={caseDetails} instruments={instruments} duration="00:48:12" data-id="element-383" />;
  }
  const getDisabledReason = () => {
    if (missingCount > 0) {
      return `Cannot finalize: ${missingCount} missing instrument${missingCount > 1 ? 's' : ''}`;
    }
    if (instruments.every(i => i.status === 'pending')) {
      return 'Cannot finalize: No scan completed';
    }
    if (!hasReviewedChecklist) {
      return 'Cannot finalize: Review checklist required';
    }
    if (finalizeUnlockTime && Date.now() < finalizeUnlockTime) {
      const remaining = Math.ceil((finalizeUnlockTime - Date.now()) / 1000);
      return `Unlocking in ${remaining}s...`;
    }
    return '';
  };
  const disabledReason = getDisabledReason();
  return <>
      <Header caseDetails={caseDetails} data-id="element-384" />

      <main className="p-8 h-[calc(100vh-80px)] bg-background" data-id="element-385">
        <div className="grid grid-cols-12 gap-6 h-full max-w-[1600px] mx-auto" data-id="element-386">
          <div className="col-span-5 flex flex-col gap-6" data-id="element-387">
            <LiveFeed isScanning={isScanning} scanStage={scanStage} data-id="element-388" />

            {/* Scan Controls - Moved from Header */}
            <div className="bg-surface rounded-card border border-border p-4 flex flex-col gap-4" data-id="element-389">
              <div className="flex items-center justify-between" data-id="element-390">
                <span className="text-sm font-bold text-text-secondary uppercase tracking-wider" data-id="element-391">
                  Scan Controls
                </span>
                <StatusBadge missingCount={missingCount} isScanning={isScanning} data-id="element-392" />
              </div>

              <button onClick={handleTriggerScan} disabled={isScanning} title={isScanning ? 'Scan in progress...' : 'Scan Tray (Ctrl+S)'} className={`
                  relative overflow-hidden h-14 w-full rounded-input font-bold text-lg tracking-wide transition-all
                  ${isScanning ? 'bg-surface-2 text-text-secondary cursor-not-allowed border border-border' : 'bg-info hover:bg-blue-500 text-white shadow-lg hover:shadow-xl'}
                `} data-id="element-393">
                {isScanning && <div className="absolute left-0 top-0 bottom-0 bg-info/30 transition-all duration-300 ease-linear" style={{
                width: `${scanProgress}%`
              }} data-id="element-394" />}
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-395">
                  {isScanning ? `Scanning ${Math.round(scanProgress)}%` : 'Scan Tray'}
                </span>
              </button>

              {!isScanning && <div className="text-center" data-id="element-396">
                  <span className="text-xs text-text-secondary" data-id="element-397">
                    Keyboard shortcut:{' '}
                    <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border rounded text-text-primary font-mono" data-id="element-398">
                      Ctrl+S
                    </kbd>
                  </span>
                </div>}
            </div>

            <ConfidenceMetrics metrics={metrics} isScanning={isScanning} lastScanTime={lastScanTime} data-id="element-399" />
          </div>

          <div className="col-span-7 flex flex-col" data-id="element-400">
            <InstrumentTable instruments={instruments} onReviewComplete={() => setHasReviewedChecklist(true)} hasReviewed={hasReviewedChecklist} data-id="element-401" />

            <div className="mt-6" data-id="element-402">
              <button onClick={handleFinalize} disabled={!canFinalize} title={canFinalize ? 'Finalize Reconciliation (Ctrl+F)' : disabledReason} className={`
                  w-full h-16 rounded-input font-bold text-lg tracking-wide transition-all flex items-center justify-center gap-3
                  ${canFinalize ? 'bg-success hover:bg-green-500 text-white shadow-lg' : 'bg-surface-2 text-text-secondary cursor-not-allowed border border-border'}
                `} data-id="element-403">
                {canFinalize ? <>
                    <CheckCircle className="w-6 h-6" data-id="element-404" />
                    Finalize Reconciliation
                  </> : <>
                    <AlertCircle className="w-6 h-6" data-id="element-405" />
                    Finalize Reconciliation
                  </>}
              </button>
              {disabledReason && <div className="mt-3 p-3 bg-critical/10 border border-critical/30 rounded-input" data-id="element-406">
                  <p className="text-center text-critical text-sm font-medium" data-id="element-407">
                    {disabledReason}
                  </p>
                </div>}
              {canFinalize && <p className="text-center text-text-secondary text-xs mt-2" data-id="element-408">
                  Keyboard shortcut: Ctrl+F
                </p>}
            </div>
          </div>
        </div>
      </main>

      {showMissingModal && <MissingInstrumentModal missingInstruments={missingInstruments} onRescan={handleRescan} onOverride={handleOverrideClick} data-id="element-409" />}

      {showOverrideForm && missingInstruments.length > 0 && <ManualOverrideForm instrument={missingInstruments[0]} onResolve={handleResolveOverride} onCancel={() => setShowOverrideForm(false)} data-id="element-410" />}

      {showScanError && <ScanErrorModal errorType={scanErrorType} onRetry={handleTriggerScan} onManualEntry={handleManualEntry} onClose={() => setShowScanError(false)} data-id="element-411" />}

      {showUnsavedModal && <UnsavedChangesModal onConfirm={() => {
      setShowUnsavedModal(false);
      if (pendingNavigation) navigate(pendingNavigation);
    }} onCancel={() => {
      setShowUnsavedModal(false);
      setPendingNavigation(null);
    }} data-id="element-412" />}

      <AIChatAssistant context="dashboard" data-id="element-413" />
    </>;
}